import { action, computed, observable, runInAction, toJS } from 'mobx';
import {
	UpdateUserProfile,
	UserInfo,
	userProfileApi,
	UserProfileCustomerInfoData,
} from 'AppClient/api/UserProfile';
import { FetchStatus } from 'servicesCommon/apiTypes';
import { RootStore } from 'AppClient/stores';
import { User } from 'oidc-client';
import { Addresses } from '../api/Bookings';

interface UserProfile {
	[key: string]: any;

	profile: {
		amr: string[];
		auth_time: number;
		email: string;
		email_verified: false;
		family_name: string;
		given_name: string;
		gs_address?: Partial<Addresses>;
		gs_customer_id: number;
		gs_customer_preferred_car_type_id: string;
		idp: string;
		name: string;
		phone_number: string;
		phone_number_verified: boolean;
		preferred_username: string;
		sid: string;
		sub: string;
	};
}

export class UserStore {
	@observable private _customerInfo: UserProfileCustomerInfoData | null = null;
	@observable private readonly _user: UserProfile;
	@observable private fetchStatus: FetchStatus = FetchStatus.IDLE;
	@observable private updatingStatus: FetchStatus = FetchStatus.IDLE;
	@observable private _userInfo: UserInfo | null = null;
	@observable public _isUpdateModalOpen = false;

	constructor(private rootStore: RootStore, private userObj: User) {
		this._user = this.userObj;
		this.fetchUserInfo(this._user.profile.sub);
		this.retrieveCustomerInfo();
	}

	@computed
	get userInfo() {
		return toJS(this._userInfo);
	}

	@computed
	get isUpdateModalOpen() {
		return this._isUpdateModalOpen;
	}

	@action.bound
	toggleModal() {
		this._isUpdateModalOpen = !this._isUpdateModalOpen;
		this.rootStore.ccStore.setCreditCards(this._userInfo ? this._userInfo.creditCards : []);
	}

	@action.bound
	async fetchUserInfo(customerUserId: string) {
		const response = await userProfileApi.getUserInfo({ customerUserId });
		const {
			data: { result, error },
		} = response;
		if (error === null) {
			this._userInfo = result;
			this.rootStore.ccStore.setCreditCards(this._userInfo ? this._userInfo.creditCards : []);
		}
	}

	@computed
	get sortedCreditCards() {
		const creditCards = this._userInfo ? this._userInfo.creditCards : [];
		const deletedCC = creditCards.filter(item => item.isDeleted);
		const expireddCC = creditCards.filter(item => item.isExpired);
		const activeCC = creditCards.filter(item => !item.isDeleted && !item.isExpired);

		return [...activeCC, ...expireddCC, ...deletedCC];
	}

	@computed
	get isUpdating() {
		return this.updatingStatus === FetchStatus.PENDING;
	}

	@computed
	get user(): UserProfile {
		return this._user;
	}

	@computed
	get customerInfoData() {
		return this._customerInfo;
	}

	@computed
	get isIdle(): boolean {
		return this.fetchStatus === FetchStatus.IDLE;
	}

	@computed
	get isPending(): boolean {
		return this.fetchStatus === FetchStatus.PENDING;
	}

	@computed
	get isSuccess(): boolean {
		return this.fetchStatus === FetchStatus.SUCCESS;
	}

	@action.bound
	private async retrieveCustomerInfo() {
		this.fetchStatus = FetchStatus.PENDING;
		try {
			const response = await userProfileApi.getCustomerInfo();
			const { error } = response.data;
			if (error) {
				this.fetchStatus = FetchStatus.FAILURE;
				return;
			}
			const { result } = response.data;
			if (result) {
				runInAction(() => {
					this.fetchStatus = FetchStatus.SUCCESS;
					this._customerInfo = result;
					this.rootStore.bookingCCStore.setUserPaymentType(result.customerPaymentMethod);
				});
			}
		} catch (e) {
			this.fetchStatus = FetchStatus.FAILURE;
		}
	}

	@action.bound
	async updateUserProfile(userData: UpdateUserProfile) {
		this.updatingStatus = FetchStatus.PENDING;
		try {
			const response = await userProfileApi.putCustomerInfo(userData);
			const {
				data: { validationErrors },
			} = response;
			if (validationErrors && validationErrors.password) {
				this.rootStore.alertStore.setAlert(validationErrors.password, 'error');
				this.updatingStatus = FetchStatus.FAILURE;
				return;
			}
			await this.fetchUserInfo(userData.id);
			runInAction(() => {
				this.toggleModal();
				this.updatingStatus = FetchStatus.SUCCESS;
			});
		} catch (_) {
			this.updatingStatus = FetchStatus.FAILURE;
		}
	}
}
